import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Clerk's getToken() is async — AuthContext registers it here on mount.
// Returns the JWT string or null if Clerk is still initialising.
let _tokenGetter = null;
export function setTokenGetter(fn) { _tokenGetter = fn; }

// Tracks whether Clerk says we're signed in (set from AuthContext). This lets
// the interceptor wait briefly for a token instead of firing a request with
// no Authorization header (which would 401 and cause the redirect loop the
// old magic-link era introduced).
let _isSignedIn = false;
export function setSignedInFlag(v) { _isSignedIn = !!v; }

// Active base ("all" or a base id) chosen in the header base switcher.
// Persisted in localStorage so it survives reloads; the interceptor
// attaches it as X-Base-Id so the backend can scope every request.
let _activeBaseId = (() => {
  try { return localStorage.getItem("ap-active-base") || "all"; } catch (_e) { return "all"; }
})();
export function setActiveBaseId(v) {
  _activeBaseId = v || "all";
  try { localStorage.setItem("ap-active-base", _activeBaseId); } catch (_e) { /* noop */ }
}
export function getActiveBaseId() { return _activeBaseId; }

async function _waitForToken(maxAttempts = 8, intervalMs = 150) {
  if (!_tokenGetter) return null;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const token = await _tokenGetter();
      if (token) return token;
    } catch (_) { /* ignore */ }
    // If Clerk doesn't even think we're signed in, no point retrying.
    if (!_isSignedIn) return null;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// Bare, interceptor-free client for genuinely PUBLIC endpoints (e.g. the
// no-login horse-report share page). The authed `api` instance blocks
// every request until Clerk's getToken() resolves; when Clerk can't load
// for the current origin (e.g. a preview domain not on the prod Clerk
// allow-list) that promise never settles and the request hangs forever.
// A buyer opening a share link is never signed in, so we deliberately
// skip auth + interceptors here.
export const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use(async (config) => {
  // Attach the active base so the backend scopes data to the selected
  // base (or "all" for cross-base views).
  try {
    config.headers["X-Base-Id"] = _activeBaseId || "all";
  } catch (_e) { /* noop */ }
  // If Clerk says we're signed in, BLOCK until we have a real token so we
  // never send an empty Authorization header (which would 401).
  if (_isSignedIn) {
    const token = await _waitForToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } else if (_tokenGetter) {
    // Best-effort attach when not yet signed in (e.g. public endpoints).
    try {
      const token = await _tokenGetter();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch (_) { /* noop */ }
  }
  return config;
});

// Intentionally NO auto-redirect on 401. With Clerk as the identity layer,
// a 401 from our backend means "token was rejected" — let the individual
// caller decide how to handle it. Auto-redirecting here caused an infinite
// loop: /login → RedirectWhenSignedIn → / → 401 → /login → …
//
// EXCEPTION: if the 401 carries `X-Signout-Reason: role_changed`, an admin
// has flipped this specific user's permissions and the current JWT is
// stale. We fire a window event so AuthContext (which owns the Clerk
// session) can call `signOut()` and route to /login. Doing it here (in
// the interceptor) keeps the logic in one place — every axios call
// gets the same behaviour whether it's the dashboard fetch or a
// button-click mutation.
export const FORCE_SIGNOUT_EVENT = "auth:force-signout";
// Module-scoped one-shot gate. First 401 with role_changed fires the
// event; subsequent 401s from the same page load are silently swallowed.
// The event listener in AuthContext also has its own gate as a
// belt-and-braces defence, but keeping the gate here means we don't
// even dispatch the CustomEvent 40 times when a page load fans out
// into many concurrent API calls.
let _forceSignoutDispatched = false;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const reason = err?.response?.headers?.["x-signout-reason"];
    if (status === 401 && reason === "role_changed" && !_forceSignoutDispatched) {
      _forceSignoutDispatched = true;
      try {
        window.dispatchEvent(new CustomEvent(FORCE_SIGNOUT_EVENT, {
          detail: {
            reason,
            message: err?.response?.data?.detail
              || "Your permissions have changed. Please sign in again.",
          },
        }));
      } catch (_) { /* SSR / non-browser safety */ }
    }
    return Promise.reject(err);
  },
);
